import { buildAdvancedCurve, canonicalSubstanceKey, type CurvePhase } from "@/domain/advancedCurveEngine";
import { buildBodyLoad, type BodyLoadGroup } from "@/domain/pkBodyLoadEngine";
import { findKnowledgeTemplate } from "@/domain/pkBodyLoadEngine";
import type { KBTemplate } from "@/lib/pharmacologyKnowledgeBase";

export type ContextRiskLevel = "monitorar" | "cautela" | "relevante" | "alto" | "emergencia";
export type ContextConfidence = "baixa" | "moderada" | "alta";

export interface TolerancePattern {
  key: string;
  label: "não estimável" | "sem sinal longitudinal forte" | "possível adaptação" | "padrão compatível com tolerância";
  confidence: ContextConfidence;
  reason: string;
}

export interface SessionSubstanceState {
  key: string;
  name: string;
  effect0to100: number;
  phase: CurvePhase;
  body?: BodyLoadGroup;
  categories: string[];
  latestDose?: any;
  doseVsRecentMedian?: number | null;
  tolerance: TolerancePattern;
  carryOver: boolean;
}

export interface ContextInteraction {
  id: string;
  title: string;
  involved: string[];
  level: ContextRiskLevel;
  confidence: ContextConfidence;
  rationale: string;
  mechanism: string;
  context: string;
  monitor: string[];
  redFlags: string[];
}

export interface ContextEffect {
  id: string;
  label: string;
  status: "plausível" | "a monitorar" | "sinal de alarme";
  rationale: string;
}

export interface RecoveryTip {
  id: string;
  title: string;
  text: string;
  urgent?: boolean;
}

export interface SessionContextSnapshot {
  generatedAt: string;
  states: SessionSubstanceState[];
  interactions: ContextInteraction[];
  effects: ContextEffect[];
  recovery: RecoveryTip[];
  notes: string[];
}

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

function num(value: unknown): number | null {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function doseTime(dose: any) {
  return new Date(dose.actual_time ?? dose.actualTime ?? "").getTime();
}

function doseName(dose: any) {
  return String(dose.substances?.name ?? dose.substance_name ?? dose.substanceName ?? "Substância").trim();
}

function median(values: number[]) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function sameUnit(a: any, b: any) {
  return String(a?.dose_unit ?? "").trim().toLowerCase() === String(b?.dose_unit ?? "").trim().toLowerCase();
}

export function inferTolerancePattern(allDoses: any[], key: string, at: Date): TolerancePattern {
  const cutoff30 = at.getTime() - 30 * DAY;
  const cutoff7 = at.getTime() - 7 * DAY;
  const relevant = allDoses
    .filter((dose) => canonicalSubstanceKey(doseName(dose)) === key)
    .filter((dose) => doseTime(dose) <= at.getTime() && doseTime(dose) >= cutoff30)
    .sort((a, b) => doseTime(a) - doseTime(b));

  if (relevant.length < 3) {
    return { key, label: "não estimável", confidence: "baixa", reason: "Poucos registros nos últimos 30 dias; não inferir tolerância fisiológica a partir disso." };
  }

  const recent = relevant.filter((dose) => doseTime(dose) >= cutoff7);
  const prior = relevant.filter((dose) => doseTime(dose) < cutoff7);
  const latest = relevant.at(-1);
  const recentAmounts = recent.filter((dose) => latest && sameUnit(dose, latest)).map((dose) => num(dose.dose_amount)).filter((value): value is number => value != null && value > 0);
  const priorAmounts = prior.filter((dose) => latest && sameUnit(dose, latest)).map((dose) => num(dose.dose_amount)).filter((value): value is number => value != null && value > 0);
  const recentMedian = median(recentAmounts);
  const priorMedian = median(priorAmounts);
  const escalation = recentMedian != null && priorMedian != null && priorMedian > 0 ? recentMedian / priorMedian : null;

  if (recent.length >= 4 && escalation != null && escalation >= 1.25) {
    return {
      key,
      label: "padrão compatível com tolerância",
      confidence: "moderada",
      reason: `${recent.length} registro(s) em 7 dias e aumento da mediana de dose em relação às semanas anteriores. É padrão longitudinal, não medida biológica de tolerância.`,
    };
  }

  if (recent.length >= 4 || relevant.length >= 12) {
    return {
      key,
      label: "possível adaptação",
      confidence: "baixa",
      reason: `Frequência de uso repetida (${recent.length} em 7 dias; ${relevant.length} em 30 dias). Isoladamente não demonstra tolerância.`,
    };
  }

  return {
    key,
    label: "sem sinal longitudinal forte",
    confidence: "baixa",
    reason: "O histórico disponível não mostra aumento de frequência/dose suficiente para sustentar hipótese mais forte.",
  };
}

function categorySet(name: string, template?: KBTemplate | null) {
  const text = [
    name,
    template?.name,
    template?.pharmacological_class,
    template?.clinical_category,
    template?.mechanism_summary,
    template?.substance_type,
    ...(template?.synonyms ?? []),
  ].filter(Boolean).join(" ").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const out = new Set<string>();
  if (/cocain|lisdex|anfet|amphetamine|metilfen|methylphen|cathinon|estimul|mdma/.test(text)) out.add("estimulante");
  if (/cocain/.test(text)) out.add("cocaina");
  if (/opio|morph|morfin|codein|oxicod|oxycod|fentan|heroin|heroina|metadon|bupren|tramad/.test(text)) out.add("opioide");
  if (/benzodia|clonaz|alpraz|diazep|loraz|midazol/.test(text)) out.add("benzodiazepinico");
  if (/alcool|alcohol|etanol|ethanol/.test(text)) out.add("alcool");
  if (/quetiap|olanzap|prometaz|prometh|zopiclon|zolpid|mirtazap|trazod/.test(text)) out.add("sedativo");
  if (out.has("opioide") || out.has("benzodiazepinico") || out.has("alcool") || out.has("sedativo")) out.add("depressor_snc");
  if (/seroton|sert|ssri|isrs|snri|sertralin|fluox|escital|parox|vortiox|venlaf|desvenlaf|dulox|mdma|tramad/.test(text)) out.add("serotonergico");
  if (/imao|maoi|monoamine oxidase inhibitor|fenelzin|tranylcyprom|moclobem/.test(text)) out.add("imao");
  return Array.from(out);
}

function strongest(levels: ContextRiskLevel[]): ContextRiskLevel {
  const order: ContextRiskLevel[] = ["monitorar", "cautela", "relevante", "alto", "emergencia"];
  return levels.sort((a, b) => order.indexOf(b) - order.indexOf(a))[0] ?? "monitorar";
}

function elevatedDose(state?: SessionSubstanceState) {
  return (state?.doseVsRecentMedian ?? 0) >= 1.5;
}

function activePhase(state?: SessionSubstanceState) {
  return !!state && ["subida", "pico", "platô", "steady-state"].includes(state.phase) && state.effect0to100 >= 25;
}

function stateContext(states: SessionSubstanceState[]) {
  return states.map((state) => {
    const residual = state.body ? `; carga PK estimada ${(state.body.remainingFraction * 100).toFixed(0)}% da massa registrada` : "";
    const origin = state.carryOver ? "; inclui carry-over de registro anterior" : "";
    return `${state.name}: ${state.phase}, efeito relativo ${state.effect0to100.toFixed(0)}/100${residual}${origin}`;
  }).join(" | ");
}

function pair(states: SessionSubstanceState[], a: string, b: string) {
  const first = states.find((state) => state.categories.includes(a));
  const second = states.find((state) => state.categories.includes(b) && state.key !== first?.key);
  return first && second ? [first, second] as const : null;
}

function addUnique<T extends { id: string }>(list: T[], item: T) {
  if (!list.some((existing) => existing.id === item.id)) list.push(item);
}

function downgradeLevel(level: ContextRiskLevel): ContextRiskLevel {
  const order: ContextRiskLevel[] = ["monitorar", "cautela", "relevante", "alto", "emergencia"];
  const index = Math.max(0, order.indexOf(level) - 1);
  return order[index];
}

function currentInteractionLevel(level: ContextRiskLevel, involved: SessionSubstanceState[]) {
  const acute = involved.some((state) => activePhase(state));
  const recentEscalation = involved.some((state) => elevatedDose(state));
  const onlyBackground = involved.length > 0 && involved.every((state) => state.carryOver && state.effect0to100 < 25 && !recentEscalation);
  if (onlyBackground && !acute) return { level: downgradeLevel(downgradeLevel(level)), background: true };
  const lowCurrent = involved.length > 0 && involved.every((state) => state.effect0to100 < 40 && !recentEscalation);
  if (lowCurrent && !acute) return { level: downgradeLevel(level), background: true };
  return { level, background: false };
}

function applyCurrentRelevance(interaction: ContextInteraction, states: SessionSubstanceState[]): ContextInteraction {
  // Subjective parent-drug curves do not estimate active metabolite exposure.
  if (interaction.id === "cocaina-alcool" || interaction.id.startsWith("material-profile-")) return interaction;
  const involved = states.filter((state) => interaction.involved.includes(state.name));
  const adjusted = currentInteractionLevel(interaction.level, involved);
  if (!adjusted.background) return interaction;
  return {
    ...interaction,
    level: adjusted.level,
    rationale: interaction.rationale + " Exposição de manutenção/carry-over sem pico modelado relevante neste momento; manter como interação potencial, mas não como evento agudo.",
    context: interaction.context + " | leitura atual: manutenção/carry-over",
  };
}

export function buildSessionContext(input: {
  allDoses: any[];
  sessionDoses?: any[];
  at?: Date;
  knowledge?: KBTemplate[];
  bodyWeightKg?: number | null;
}): SessionContextSnapshot {
  const at = input.at ?? new Date();
  const allDoses = input.allDoses.filter((dose) => Number.isFinite(doseTime(dose)) && doseTime(dose) <= at.getTime());
  const sessionDoses = input.sessionDoses ?? [];
  const knowledge = input.knowledge ?? [];
  const curve = buildAdvancedCurve(allDoses, { now: at, windowHours: 72, stepMin: 10 });
  const body = buildBodyLoad(allDoses, { now: at, knowledge, bodyWeightKg: input.bodyWeightKg ?? undefined } as any);
  const bodyByKey = new Map(body.groups.map((group) => [canonicalSubstanceKey(group.canonicalName), group]));
  const sessionKeys = new Set(sessionDoses.map((dose) => canonicalSubstanceKey(doseName(dose))));
  const curveByKey = new Map(curve.series.map((series) => [series.canonicalKey, series]));
  const keys = new Set([...curveByKey.keys(), ...bodyByKey.keys(), ...sessionKeys]);

  const states: SessionSubstanceState[] = [];
  for (const key of keys) {
    const series = curveByKey.get(key);
    const bodyGroup = bodyByKey.get(key);
    const same = allDoses.filter((dose) => canonicalSubstanceKey(doseName(dose)) === key).sort((a, b) => doseTime(a) - doseTime(b));
    const latestDose = same.at(-1);
    const previous = same.slice(0, -1).filter((dose) => doseTime(dose) >= at.getTime() - 30 * DAY && latestDose && sameUnit(dose, latestDose));
    const priorMedian = median(previous.map((dose) => num(dose.dose_amount)).filter((value): value is number => value != null && value > 0));
    const latestAmount = num(latestDose?.dose_amount);
    const ratio = priorMedian != null && latestAmount != null && priorMedian > 0 ? latestAmount / priorMedian : null;
    const name = series?.label ?? bodyGroup?.canonicalName ?? doseName(latestDose);
    const template = findKnowledgeTemplate(name, knowledge);
    const effect = Number(series?.nowValue ?? 0);
    const bodyActive = (bodyGroup?.remainingFraction ?? 0) >= 0.01;
    if (effect < 3 && !bodyActive && !sessionKeys.has(key)) continue;
    states.push({
      key,
      name,
      effect0to100: effect,
      phase: series?.nowPhase ?? "residual",
      body: bodyGroup,
      categories: categorySet(name, template),
      latestDose,
      doseVsRecentMedian: ratio,
      tolerance: inferTolerancePattern(allDoses, key, at),
      carryOver: !sessionKeys.has(key),
    });
  }
  states.sort((a, b) => b.effect0to100 - a.effect0to100 || (b.body?.remainingFraction ?? 0) - (a.body?.remainingFraction ?? 0));

  const interactions: ContextInteraction[] = [];
  const depressants = states.filter((state) => state.categories.includes("depressor_snc"));
  const opioids = states.filter((state) => state.categories.includes("opioide"));
  const benzos = states.filter((state) => state.categories.includes("benzodiazepinico"));
  const alcohol = states.find((state) => state.categories.includes("alcool"));
  const cocaine = states.find((state) => state.categories.includes("cocaina"));
  const stimulants = states.filter((state) => state.categories.includes("estimulante"));
  const serotonergic = states.filter((state) => state.categories.includes("serotonergico"));
  const maoi = states.find((state) => state.categories.includes("imao"));

  for (const opioid of opioids) {
    for (const depressant of depressants.filter((state) => state.key !== opioid.key)) {
      const bothActive = activePhase(opioid) && activePhase(depressant);
      const thirdDepressant = depressants.length >= 3;
      const level: ContextRiskLevel = thirdDepressant || (bothActive && (elevatedDose(opioid) || elevatedDose(depressant))) ? "alto" : "relevante";
      addUnique(interactions, {
        id: `resp-${opioid.key}-${depressant.key}`,
        title: `${opioid.name} + ${depressant.name}: depressão do SNC/respiração`,
        involved: [opioid.name, depressant.name],
        level,
        confidence: "alta",
        rationale: "Opioides podem deprimir ventilação; outros depressores do SNC podem somar sedação e reduzir a margem de segurança. O risco muda com fase, carga residual e outros depressores presentes.",
        mechanism: "Depressão respiratória opioide somada a sedação/depressão central por outro agente.",
        context: stateContext([opioid, depressant]),
        monitor: ["frequência e padrão respiratório", "nível de consciência", "SpO₂ quando disponível", "vômitos/aspiração"],
        redFlags: ["não acorda ou responde muito pouco", "respiração lenta, irregular ou ruidosa", "lábios arroxeados", "convulsão"],
      });
    }
  }

  if (benzos.length && alcohol) {
    const benzo = benzos[0];
    addUnique(interactions, {
      id: `benzo-alcool-${benzo.key}`,
      title: `${benzo.name} + ${alcohol.name}: sedação aditiva`,
      involved: [benzo.name, alcohol.name],
      level: activePhase(benzo) && activePhase(alcohol) ? "alto" : "relevante",
      confidence: "alta",
      rationale: "A combinação pode aumentar sonolência, amnésia, ataxia, rebaixamento do nível de consciência e risco de aspiração; outros depressores elevam mais o risco.",
      mechanism: "Potenciação de vias inibitórias centrais por mecanismos diferentes e parcialmente aditivos.",
      context: stateContext([benzo, alcohol]),
      monitor: ["consciência", "coordenação", "respiração", "vômitos"],
      redFlags: ["difícil de despertar", "respiração anormal", "vômitos com rebaixamento", "queda/trauma importante"],
    });
  }

  if (cocaine && alcohol) {
    addUnique(interactions, {
      id: "cocaina-alcool",
      title: `${cocaine.name} + ${alcohol.name}: sobrecarga cardiovascular e cocaetileno`,
      involved: [cocaine.name, alcohol.name],
      level: activePhase(cocaine) && activePhase(alcohol) ? "alto" : "relevante",
      confidence: "moderada",
      rationale: "A coexposição pode formar cocaetileno, metabólito psicoativo com meia-vida geralmente maior que a da cocaína, prolongando efeitos e preocupação cardiovascular. Sensação de menor ansiedade ou maior controle é variável e não demonstra menor toxicidade; prejuízo psicomotor e sedação podem ser mascarados. Formação, quantidade e duração individuais não são calculadas pelo modelo. Caudas das substâncias originais não excluem exposição ao metabólito.",
      mechanism: "Interação farmacocinética: transesterificação hepática de cocaína na presença de etanol. Cocaetileno tem atividade dopaminérgica e efeitos sobre canais cardíacos, além da carga simpaticomimética da combinação. Evidência: revisão sistemática de 2024, PMID 38592322; nenhuma concentração ou curva do metabólito é inferida.",
      context: stateContext([cocaine, alcohol]),
      monitor: ["frequência cardíaca", "pressão arterial", "dor torácica", "temperatura", "agitação"],
      redFlags: ["dor torácica intensa", "desmaio", "palpitações persistentes com mal-estar", "convulsão", "confusão grave"],
    });
  }

  if (stimulants.length >= 2) {
    const involved = stimulants.slice(0, 3);
    const burden = involved.reduce((sum, state) => sum + state.effect0to100, 0);
    addUnique(interactions, {
      id: `multi-stim-${involved.map((state) => state.key).join("-")}`,
      title: "Carga simpaticomimética cumulativa",
      involved: involved.map((state) => state.name),
      level: burden >= 130 || involved.some(elevatedDose) ? "relevante" : "cautela",
      confidence: "moderada",
      rationale: "Mais de um estimulante está ativo ou em carry-over. O risco não é apenas a pior dupla: efeitos autonômicos e neuropsiquiátricos podem se somar.",
      mechanism: "Somação de sinalização catecolaminérgica/simpaticomimética.",
      context: stateContext(involved),
      monitor: ["FC", "PA", "dor torácica", "ansiedade/agitação", "temperatura", "sono"],
      redFlags: ["dor torácica", "síncope", "convulsão", "hipertermia importante", "agitação/confusão grave"],
    });
  }

  for (const stimulant of stimulants) {
    for (const opioid of opioids) {
      if (stimulant.key === opioid.key) continue;
      addUnique(interactions, {
        id: `stim-opioid-${stimulant.key}-${opioid.key}`,
        title: `${stimulant.name} + ${opioid.name}: mascaramento entre estimulante e opioide`,
        involved: [stimulant.name, opioid.name],
        level: activePhase(stimulant) && activePhase(opioid) ? "relevante" : "cautela",
        confidence: "moderada",
        rationale: "Efeitos subjetivamente opostos não se cancelam de forma protetora. O estimulante pode mascarar sedação enquanto o risco respiratório do opioide persiste; o opioide pode reduzir percepção de hiperestimulação.",
        mechanism: "Coexistência de drive simpaticomimético e agonismo opioide. Esse padrão é coloquialmente associado ao termo ‘speedball’, mas intensidade subjetiva não define segurança.",
        context: stateContext([stimulant, opioid]),
        monitor: ["respiração", "consciência", "FC/PA", "dor torácica", "temperatura"],
        redFlags: ["respiração lenta/irregular", "não acorda", "dor torácica", "desmaio", "convulsão"],
      });
    }
  }

  if (serotonergic.length >= 2) {
    const involved = serotonergic.slice(0, 4);
    const hasMaoi = !!maoi;
    addUnique(interactions, {
      id: `sero-${involved.map((state) => state.key).join("-")}`,
      title: hasMaoi ? "Carga serotoninérgica com IMAO" : "Carga serotoninérgica combinada",
      involved: involved.map((state) => state.name),
      level: hasMaoi ? "alto" : "cautela",
      confidence: hasMaoi ? "alta" : "moderada",
      rationale: hasMaoi
        ? "A presença de IMAO altera substancialmente a margem de segurança de agentes serotoninérgicos."
        : "Múltiplos agentes serotoninérgicos aumentam plausibilidade de efeitos autonômicos/neuromusculares, mas mecanismo isolado não prova síndrome serotoninérgica.",
      mechanism: "Aumento convergente de sinalização serotoninérgica por mecanismos distintos.",
      context: stateContext(involved),
      monitor: ["agitação", "tremor", "sudorese", "temperatura", "clônus/hiperreflexia"],
      redFlags: ["clônus importante", "hipertermia", "rigidez", "confusão grave", "convulsão"],
    });
  }

  if (depressants.length >= 3) {
    addUnique(interactions, {
      id: "multi-depressant",
      title: `${depressants.length} depressores do SNC simultaneamente relevantes`,
      involved: depressants.map((state) => state.name),
      level: "alto",
      confidence: "alta",
      rationale: "O risco cumulativo não deve ser reduzido à pior interação par-a-par. Sedação, rebaixamento e comprometimento respiratório/aspiração podem se somar.",
      mechanism: "Convergência de múltiplos mecanismos depressores do sistema nervoso central.",
      context: stateContext(depressants),
      monitor: ["consciência", "respiração", "SpO₂", "vômitos", "coordenação"],
      redFlags: ["não desperta", "respiração anormal", "cianose", "aspiração", "convulsão"],
    });
  }

  // Agregação clínica: uma mesma consequência não deve virar uma pilha de cards pairwise.
  if (opioids.length && depressants.length) {
    const involved = Array.from(new Map([...opioids, ...depressants].map((state) => [state.key, state])).values());
    const acuteCount = involved.filter(activePhase).length;
    const level: ContextRiskLevel = acuteCount >= 2 || depressants.length >= 3 ? "alto" : acuteCount === 1 ? "relevante" : "cautela";
    const aggregate: ContextInteraction = {
      id: "aggregate-cns-respiratory",
      title: "Carga depressora do SNC/respiratória",
      involved: involved.map((state) => state.name),
      level,
      confidence: "alta",
      rationale: "Há opioide(s) e outro(s) depressor(es) do SNC presentes na janela atual. O risco é agregado por mecanismo; não se soma alerta individual de cada dupla.",
      mechanism: "Convergência de depressão respiratória opioide + sedação/depressão central por outros agentes.",
      context: stateContext(involved),
      monitor: ["frequência/padrão respiratório", "consciência", "SpO₂ quando disponível", "vômitos/aspiração"],
      redFlags: ["não desperta", "respiração lenta/irregular/ruidosa", "cianose", "convulsão"],
    };
    interactions.splice(0, interactions.length, ...interactions.filter((item) => !item.id.startsWith("resp-") && item.id !== "multi-depressant" && !item.id.startsWith("benzo-alcool-")));
    interactions.push(aggregate);
  }

  // Material profile interpretation: only add documented/reported composition risks when the
  // associated exposure is recent enough for the mechanism; never infer adulterants from appearance.
  const materialDoseCandidates = [...allDoses]
    .filter((dose) => dose.material_profile_snapshot && dose.material_profile_risk_interpretation)
    .sort((a, b) => doseTime(b) - doseTime(a));
  const seenMaterialExposure = new Set<string>();
  const materialEffects: ContextEffect[] = [];
  for (const dose of materialDoseCandidates) {
    const elapsedHours = Math.max(0, (at.getTime() - doseTime(dose)) / HOUR);
    const profile = dose.material_profile_snapshot;
    const interpretation = dose.material_profile_risk_interpretation;
    const sampleKey = String(profile.id ?? profile.profile_name ?? dose.id) + ":" + String(dose.substance_id ?? dose.substance_name);
    if (seenMaterialExposure.has(sampleKey)) continue;
    seenMaterialExposure.add(sampleKey);
    const findings = Array.isArray(interpretation.compositionFindings) ? interpretation.compositionFindings : [];
    for (const component of findings) {
      const axes = Array.isArray(component.riskAxes) ? component.riskAxes : [];
      const chronicOrDelayed = axes.some((axis: string) => /hemat|imun|vascul|cut.neo|renal|hep.tico|toxicidade cr.nica/i.test(axis + " " + component.name));
      if (elapsedHours > (chronicOrDelayed ? 30 * 24 : 24)) continue;
      const status = String(component.status ?? "unknown");
      const isConfirmed = status === "tested_detected" && profile.composition_status === "laboratory_test";
      const isReported = status === "reported";
      const confidence: ContextConfidence = isConfirmed && component.confidence === "alta" ? "alta" : isReported ? "moderada" : "baixa";
      const level: ContextRiskLevel = isConfirmed && chronicOrDelayed ? "relevante" : isConfirmed ? "cautela" : "monitorar";
      const componentEffects = Array.isArray(component.clinicalEffects) ? component.clinicalEffects : [];
      const redFlags = /levamisol/i.test(component.name)
        ? ["púrpura/necrose cutânea, febre, infecção ou sinais sistêmicos"]
        : /opioide|fentanil/i.test(component.name)
          ? ["respiração lenta/irregular, cianose ou rebaixamento de consciência"]
          : /benzoca/i.test(component.name)
            ? ["cianose/hipoxemia desproporcional ou dispneia"]
            : /lidoca|proca/i.test(component.name)
              ? ["convulsão, síncope ou arritmia em contexto compatível"]
              : [];
      addUnique(interactions, {
        id: "material-profile-" + String(profile.id ?? profile.profile_name) + "-" + canonicalSubstanceKey(component.name),
        title: String(profile.profile_name ?? "Perfil de material") + ": contexto clínico por " + String(component.name),
        involved: [doseName(dose)],
        level,
        confidence,
        rationale: component.mechanism + " Status da informação: " + status + ". " + (isConfirmed ? "Componente marcado como detectado em análise." : "Componente não está confirmado analiticamente; não tratar como presença certa.") + " Exposição associada há " + elapsedHours.toFixed(1) + " h; para este mecanismo, a relevância pode ser tardia/residual.",
        mechanism: component.mechanism,
        context: "Perfil de material: " + String(profile.profile_name ?? "sem nome") + " · composição " + String(profile.composition_status ?? "desconhecida") + " · confiança global " + String(profile.composition_confidence ?? "baixa"),
        monitor: axes.concat(componentEffects).slice(0, 8),
        redFlags,
      });
      if (componentEffects.length) {
        materialEffects.push({
          id: "material-effect-" + String(profile.id ?? profile.profile_name) + "-" + canonicalSubstanceKey(component.name),
          label: String(component.name) + ": " + componentEffects.slice(0, 2).join("; "),
          status: redFlags.length ? "a monitorar" : "plausível",
          rationale: "Baseado no perfil de amostra associado à exposição de " + doseName(dose) + ". Status: " + status + "; confiança " + confidence + ". Não equivale à confirmação de que esses sintomas ocorrerão.",
        });
      }
    }
    if (elapsedHours <= 24 && Array.isArray(interpretation.potentialEffects) && interpretation.potentialEffects.length) {
      materialEffects.push({
        id: "material-profile-base-" + String(profile.id ?? profile.profile_name),
        label: String(profile.canonical_substance_name ?? doseName(dose)) + ": fenótipo farmacológico basal da substância",
        status: "plausível",
        rationale: interpretation.potentialEffects.join("; ") + " Considerar dose, via, sono, história individual e coexposições; pureza isolada não prevê intensidade subjetiva.",
      });
    }
  }

  const currentAdjusted = interactions.map((interaction) => applyCurrentRelevance(interaction, states));
  const deduped = new Map<string, ContextInteraction>();
  for (const interaction of currentAdjusted) {
    const family = interaction.id.startsWith("sero-") ? "serotonergic" :
      interaction.id.startsWith("multi-stim-") ? "sympathomimetic" :
      interaction.id.startsWith("stim-opioid-") ? "stimulant-opioid" :
      interaction.id.startsWith("cocaina-alcool") ? "cocaine-alcohol" :
      interaction.id.startsWith("aggregate-cns-respiratory") ? "cns-respiratory" : interaction.id;
    const previous = deduped.get(family);
    if (!previous) { deduped.set(family, interaction); continue; }
    deduped.set(family, {
      ...previous,
      level: strongest([previous.level, interaction.level]),
      involved: Array.from(new Set([...previous.involved, ...interaction.involved])),
      monitor: Array.from(new Set([...previous.monitor, ...interaction.monitor])).slice(0, 8),
      redFlags: Array.from(new Set([...previous.redFlags, ...interaction.redFlags])).slice(0, 6),
      context: [previous.context, interaction.context].filter(Boolean).join(" | "),
    });
  }
  const interactionsFinal = Array.from(deduped.values()).sort((a, b) => {
    const order: ContextRiskLevel[] = ["monitorar", "cautela", "relevante", "alto", "emergencia"];
    return order.indexOf(b.level) - order.indexOf(a.level);
  });

  const effects: ContextEffect[] = [...materialEffects];
  const stimulantBurden = stimulants.reduce((sum, state) => sum + state.effect0to100, 0);
  if (stimulantBurden >= 25) {
    addUnique(effects, { id: "tachycardia", label: "Taquicardia / elevação de pressão", status: stimulantBurden >= 90 ? "a monitorar" : "plausível", rationale: "Há atividade estimulante/simpaticomimética relevante na fase atual. Sem sinal vital medido, isto é previsão qualitativa, não evento confirmado." });
    addUnique(effects, { id: "agitation", label: "Ansiedade, inquietação ou agitação", status: "plausível", rationale: "Compatível com carga catecolaminérgica, especialmente em subida/pico ou com múltiplos estimulantes." });
  }
  if (depressants.length) {
    addUnique(effects, { id: "sedation", label: "Sedação, ataxia ou redução de vigilância", status: depressants.length >= 2 ? "a monitorar" : "plausível", rationale: "Há ao menos um depressor do SNC ativo/residual; múltiplos depressores aumentam preocupação." });
  }
  if (opioids.length && depressants.length >= 2) {
    addUnique(effects, { id: "respiratory", label: "Depressão respiratória", status: "sinal de alarme", rationale: "Não é esperado como efeito obrigatório, mas é o desfecho crítico a vigiar quando opioide se sobrepõe a outros depressores." });
  }
  if (cocaine && alcohol) {
    addUnique(effects, { id: "cardiac-stress", label: "Palpitações / sobrecarga cardiovascular prolongada", status: "a monitorar", rationale: "Cocaína + etanol pode formar cocaetileno. Efeitos psicoativos podem durar mais, sem relação confiável entre sensação de controle e risco cardíaco. A duração individual é incerta; dor torácica, síncope ou arritmia sintomática exigem avaliação urgente." });
  }
  if (serotonergic.length >= 2) {
    addUnique(effects, { id: "serotonergic", label: "Tremor, sudorese, hiperreflexia/agitação", status: "plausível", rationale: "Há carga serotoninérgica combinada. O conjunto de sinais e exame clínico é que determina toxicidade; combinação isolada não fecha diagnóstico." });
  }

  const recovery: RecoveryTip[] = [];
  if (stimulants.length) {
    recovery.push({ id: "stim-rest", title: "Reduzir estímulos adicionais", text: "Evitar adicionar outros estimulantes/cafeína; permanecer em ambiente ventilado e menos quente. Dor torácica, desmaio, convulsão, hipertermia ou confusão importante → atendimento de emergência." });
    recovery.push({ id: "hydration", title: "Hidratação sem excesso", text: "Se estiver acordado e conseguindo engolir, pequenos goles de água podem ajudar. Evite ingerir grande volume rapidamente; hidratação não reverte toxicidade." });
  }
  if (depressants.length) {
    recovery.push({ id: "no-alone", title: "Não deixar a pessoa desacompanhada se houver sedação", text: "Observe resposta e respiração. Não tente ‘compensar’ sedação com outro estimulante." });
    recovery.push({ id: "recovery-position", title: "Posição lateral de segurança", text: "Se estiver inconsciente ou muito sonolento, mas respirando normalmente, colocar de lado ajuda a reduzir risco de aspiração. Se não respira normalmente ou não desperta, acione o SAMU 192 imediatamente.", urgent: true });
  }
  if (opioids.length) {
    recovery.push({ id: "opioid-emergency", title: "Suspeita de overdose por opioide", text: "Respiração lenta/irregular, pupilas puntiformes com rebaixamento ou incapacidade de despertar são sinais críticos. SAMU 192; naloxona, quando disponível e usada por pessoa treinada, não substitui atendimento de emergência.", urgent: true });
  }
  if (alcohol) {
    recovery.push({ id: "alcohol-depressants", title: "Álcool + sedativos", text: "Evite acrescentar benzodiazepínicos, opioides ou outros sedativos. Vômitos com rebaixamento, dificuldade para respirar ou incapacidade de despertar exigem emergência." });
  }

  return {
    generatedAt: at.toISOString(),
    states,
    interactions: interactionsFinal,
    effects,
    recovery,
    notes: [
      "Risco é contextual: considera fase modelada, carga PK estimada, carry-over de doses anteriores e padrão individual de dose quando há histórico suficiente.",
      "Curvas e carga corporal são estimativas. Ausência de alerta não significa ausência de risco.",
      "Tolerância é inferida como padrão longitudinal e nunca como porcentagem fisiológica medida.",
    ],
  };
}
